import React from 'react';
import {
  ErrorMessage,
  MultiSelect,
  SelectDropdown,
  Textbox,
  TextInput,
} from '@gsa/afp-component-library';
import { isEmpty } from 'lodash';
import * as PropTypes from 'prop-types';
import { Controller, FormProvider, useFormContext } from 'react-hook-form';
import {
  getRules,
  renderCodeError,
  renderTitleError,
  renderSequenceError,
  validate,
} from '../../utilities/validations/error-rules';
import InteractiveCheck from '../InteractiveCheck/InteractiveCheck';
import getFormContext from './catalog-codes-form-context';
import { StandardFieldset } from './standard-fieldset';
import { OPERATIONS, SUBJECTS } from '../../utilities/constants';

const selectionTypeOptions = [
  { label: 'M - Multiple', value: 'M' },
  { label: 'S - Single', value: 'S' },
];

const allowNewBidOptions = [
  { label: '-Select-', value: '' },
  { label: 'Yes', value: 'Y' },
  { label: 'No', value: 'N' },
];

// Stored values can be null (never set) or numeric (sequence), while an input
// needs a string to stay controlled.
const toInputValue = (value) =>
  value === null || value === undefined ? '' : `${value}`;

function validateUSPhoneNumber(phoneNumber) {
  const regex = /^(\+\d{1,2}\s)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/;
  return regex.test(phoneNumber);
}

function validateEmailAddress(email) {
  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,63}$/;
  return regex.test(email);
}

// eslint-disable-next-line react/prop-types
const ContactForm = ({ contactType, title, required = true }) => {
  const { control } = useFormContext();
  return (
    <StandardFieldset>
      <h4
        className="standard-section-header"
        style={{
          fontSize: '0.75em',
          textTransform: 'uppercase',
          fontWeight: 700,
          color: '#005ea2',
        }}
      >
        {title}
      </h4>
      <div className="grid-row">
        <div className="grid-col-5">
          <Controller
            control={control}
            name={`vehicleGroupPOCs.${contactType}.firstName`}
            defaultValue=""
            rules={{ required: required ? 'This is a required field.' : false }}
            render={({ field, fieldState: { error } }) => (
              <TextInput
                style={{ width: 248 }}
                required={required}
                name={`vehicleGroupPOCs.${contactType}.firstName`}
                onChange={field.onChange}
                onBlur={field.onBlur}
                value={field.value}
                minLength={2}
                maxLength={120}
                label="First name"
                errorMessage={error?.message}
              />
            )}
          />
        </div>
        <div className="grid-col-5">
          <Controller
            control={control}
            name={`vehicleGroupPOCs.${contactType}.lastName`}
            defaultValue=""
            rules={{ required: required ? 'This is a required field.' : false }}
            render={({ field, fieldState: { error } }) => (
              <TextInput
                style={{ width: 248 }}
                required={required}
                name={`vehicleGroupPOCs.${contactType}.lastName`}
                onChange={field.onChange}
                onBlur={field.onBlur}
                value={field.value}
                maxLength={120}
                label="Last name"
                errorMessage={error?.message}
              />
            )}
          />
        </div>
      </div>

      <div className="grid-row">
        <div className="grid-col-5">
          <Controller
            control={control}
            name={`vehicleGroupPOCs.${contactType}.email`}
            defaultValue=""
            rules={{
              required: required ? 'This is a required field.' : false,
              validate: (value) =>
                !value ||
                validateEmailAddress(value) ||
                'Invalid email address',
            }}
            render={({ field, fieldState: { error } }) => (
              <TextInput
                style={{ width: 248 }}
                required={required}
                name={`vehicleGroupPOCs.${contactType}.email`}
                onChange={field.onChange}
                onBlur={field.onBlur}
                value={field.value}
                maxLength={120}
                label="Email"
                errorMessage={error?.message}
              />
            )}
          />
        </div>
        <div className="grid-col-5">
          <Controller
            control={control}
            name={`vehicleGroupPOCs.${contactType}.phoneNumber`}
            defaultValue=""
            rules={{
              required: required ? 'This is a required field.' : false,
              validate: (value) =>
                !value ||
                validateUSPhoneNumber(value) ||
                'Invalid phone number',
            }}
            render={({ field, fieldState: { error } }) => (
              <TextInput
                style={{ width: 248 }}
                required={required}
                name={`vehicleGroupPOCs.${contactType}.phoneNumber`}
                onChange={field.onChange}
                onBlur={field.onBlur}
                value={field.value}
                maxLength={16}
                label="Phone number"
                errorMessage={error?.message}
              />
            )}
          />
        </div>
      </div>
    </StandardFieldset>
  );
};

const CatalogCodeForm = ({
  formId,
  selectedCatalogCode,
  metadataOfSelectedCategory,
  catalogOptionsForTagsAndParentCodes,
  isNew,
  equipmentCategorySchema,
  fuelTypeCategorySchema,
  onSubmit,
  // eslint-disable-next-line react/prop-types
  ability,
}) => {
  const hasParentCategory = !!metadataOfSelectedCategory.parentCategory;
  const methods = getFormContext(
    metadataOfSelectedCategory.category,
    selectedCatalogCode,
    {
      // eslint-disable-next-line react/prop-types
      canAddCoPocs: ability?.can(
        OPERATIONS.Manage,
        SUBJECTS.VehicleGroupEngPOCs,
      ),
      requiresParentCode: hasParentCategory,
      validateCode: isNew,
    },
  );
  const {
    formState: { errors, touchedFields },
    handleSubmit,
    control,
    watch,
  } = methods;

  const renderRequiredComponent = (value) => {
    return (
      <h2 className="font-sans-md">
        {value} <span className="usa-hint usa-hint--required">*</span>
      </h2>
    );
  };

  const hasTagOptions = !!metadataOfSelectedCategory.categoryOfTags;
  const tagOptions = catalogOptionsForTagsAndParentCodes?.tagCodes;
  const parentOptions = catalogOptionsForTagsAndParentCodes?.parentCodes;
  const catalogTagDefaultValue = selectedCatalogCode
    ? selectedCatalogCode.tags?.value
    : [];

  // code
  const isDirty = touchedFields?.code || !isEmpty(errors?.code?.message);
  const rules = getRules(watch('code'), metadataOfSelectedCategory.category);
  const isValid = validate(rules);
  const hasCodeError = !isValid && isDirty;

  // Title
  const isTitleDirty =
    !isEmpty(watch('title')) || !isEmpty(errors?.title?.message);
  const titleRules = getRules(watch('title'), 'Title');
  const isTitleValid = validate(titleRules);
  const hasTittleError = !isTitleValid && isTitleDirty;

  // Sequence
  const isSequenceDirty =
    !isEmpty(watch('sequence')) || !isEmpty(errors?.sequence?.message);
  const Sequencerules = getRules(watch('sequence'), 'Sequence');
  const isSequenceValid = validate(Sequencerules);
  const hasSequenceError = !isSequenceValid && isSequenceDirty;

  const allowNewBidValue = (defaultValue) => {
    if (isNew) {
      return '';
    }
    return defaultValue ? 'Y' : 'N';
  };

  return (
    <FormProvider {...methods}>
      <form id={formId} onSubmit={handleSubmit(onSubmit)} noValidate>
        <div
          className={`grid-row padding-bottom-2 ${
            hasCodeError ? 'padding-left-2' : null
          }`}
        >
          <div className="grid-col-4 ">
            {isNew ? (
              renderRequiredComponent('Code')
            ) : (
              <h2 className="font-sans-md">Code</h2>
            )}
            {isNew ? (
              <>
                <Controller
                  name="code"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextInput
                      {...field}
                      data-testid="catalog_code_code"
                      aria-label="Code"
                      aria-required="true"
                      type="text"
                      errorMessage={renderCodeError(
                        hasCodeError,
                        'Make sure the code meets the requirements listed below.',
                      )}
                    />
                  )}
                />
                {hasCodeError && (
                  <InteractiveCheck checks={rules} isDirty={isDirty} />
                )}
              </>
            ) : (
              selectedCatalogCode?.code
            )}
          </div>
        </div>

        <div
          className={`grid-row padding-bottom-2 ${
            hasTittleError ? 'padding-left-2' : null
          }`}
        >
          {/* Title */}
          <div className="grid-col-4">
            {renderRequiredComponent('Title')}
            <Controller
              name="title"
              control={control}
              defaultValue={isNew ? '' : selectedCatalogCode?.title}
              render={({ field }) => (
                <TextInput
                  {...field}
                  data-testid="catalog_title_code"
                  aria-label="Title"
                  aria-required="true"
                  type="text"
                  required
                  errorMessage={renderTitleError(hasTittleError)}
                />
              )}
            />
            {hasTittleError && (
              <InteractiveCheck checks={titleRules} isDirty={isTitleDirty} />
            )}
          </div>
        </div>
        {/* Sequence */}
        {!isEmpty(equipmentCategorySchema) && (
          <>
            <div
              className={`grid-row padding-bottom-2 ${
                hasSequenceError ? 'padding-left-2' : null
              }`}
            >
              <div className="grid-col-4">
                {renderRequiredComponent('Sequence')}
                <Controller
                  name="sequence"
                  control={control}
                  defaultValue={
                    isNew
                      ? '0'
                      : toInputValue(
                          selectedCatalogCode?.additionalProps?.sequence,
                        )
                  }
                  render={({ field }) => (
                    <TextInput
                      {...field}
                      value={toInputValue(field.value)}
                      data-testid="eq_edit_sequence"
                      aria-label="Sequence"
                      aria-required="true"
                      type="text"
                      errorMessage={renderSequenceError(
                        hasSequenceError,
                        'Make sure the Sequence meets the requirements listed below.',
                      )}
                    />
                  )}
                />
                {hasSequenceError && (
                  <InteractiveCheck
                    checks={Sequencerules}
                    isDirty={isSequenceDirty}
                  />
                )}
              </div>
            </div>
            <div className="grid-row padding-bottom-2">
              <div className="grid-col-4">
                {renderRequiredComponent('Selection Type')}
                <Controller
                  data-testid="catalog_selectionType"
                  name="selectionType"
                  control={control}
                  defaultValue={
                    isNew
                      ? selectionTypeOptions[0].value
                      : selectedCatalogCode?.additionalProps?.selectionType
                  }
                  render={({ field }) => (
                    <SelectDropdown
                      data-testid="catalog_selectionType_sel"
                      aria-label="Selection Type"
                      aria-required="true"
                      onChange={(ev) => field.onChange(ev.target.value)}
                      options={selectionTypeOptions}
                      value={field.value}
                    />
                  )}
                />
                {errors?.selectionType?.message && (
                  <ErrorMessage>{errors.selectionType.message}</ErrorMessage>
                )}
              </div>
            </div>
          </>
        )}

        {!isEmpty(fuelTypeCategorySchema) && (
          <>
            <div className="grid-row padding-bottom-2">
              <div className="grid-col-4">
                {renderRequiredComponent('Fast Code')}
                <Controller
                  name="fastCode"
                  control={control}
                  defaultValue={
                    isNew ? '' : selectedCatalogCode?.additionalProps?.fastCode
                  }
                  render={({ field }) => (
                    <Textbox
                      {...field}
                      value={toInputValue(field.value)}
                      data-testid="catalog_fastCode"
                      aria-label="Fast Code"
                      aria-required="true"
                      type="text"
                      error={errors?.fastCode}
                    />
                  )}
                />
                {errors?.fastCode?.message && (
                  <ErrorMessage>{errors.fastCode.message}</ErrorMessage>
                )}
              </div>
            </div>

            <div className="grid-row padding-bottom-2">
              <div className="grid-col-4">
                {renderRequiredComponent('Short Hand')}
                <Controller
                  name="shortHand"
                  control={control}
                  defaultValue={
                    isNew ? '' : selectedCatalogCode?.additionalProps?.shortHand
                  }
                  render={({ field }) => (
                    <Textbox
                      {...field}
                      value={toInputValue(field.value)}
                      data-testid="catalog_shortHand"
                      aria-label="Short Hand"
                      aria-required="true"
                      type="text"
                      error={errors?.shortHand}
                    />
                  )}
                />
                {errors?.shortHand?.message && (
                  <ErrorMessage>{errors.shortHand.message}</ErrorMessage>
                )}
              </div>
            </div>

            <div className="grid-row padding-bottom-2">
              <div className="grid-col-4">
                {renderRequiredComponent('New Bids')}
                <Controller
                  data-testid="catalog_new_bid"
                  name="allowNewBids"
                  control={control}
                  defaultValue={allowNewBidValue(
                    selectedCatalogCode?.additionalProps?.allowNewBids,
                  )}
                  render={({ field }) => (
                    <SelectDropdown
                      data-testid="catalog_newbids_sel"
                      aria-label="Allow New bids"
                      aria-required="true"
                      onChange={(ev) => field.onChange(ev.target.value)}
                      options={allowNewBidOptions}
                      value={field.value}
                    />
                  )}
                />
                {errors?.allowNewBids?.message && (
                  <ErrorMessage>{errors.allowNewBids.message}</ErrorMessage>
                )}
              </div>
            </div>
          </>
        )}

        {hasParentCategory && (
          <div className="grid-row padding-bottom-2">
            <div className="grid-col-4">
              {renderRequiredComponent(
                `${metadataOfSelectedCategory.parentCategory}`,
              )}
              <Controller
                data-testid="catalog_code_parent"
                name="parentCode"
                control={control}
                defaultValue={isNew ? '' : selectedCatalogCode?.parentCode}
                render={({ field }) => (
                  <SelectDropdown
                    data-testid="catalog_code_parent_sel"
                    aria-label={` Select ${metadataOfSelectedCategory.parentCategory}`}
                    required="true"
                    onChange={(ev) => field.onChange(ev.target.value)}
                    options={[
                      { label: '- Select -', value: '' },
                      ...parentOptions,
                    ]}
                    value={field.value ?? ''}
                    ref={field.ref}
                  />
                )}
              />
              {errors?.parentCode?.message && (
                <ErrorMessage>{errors.parentCode.message}</ErrorMessage>
              )}
            </div>
          </div>
        )}

        <div className="grid-row padding-bottom-2">
          <div className="grid-col-6">
            <h2 className="font-sans-md">Description</h2>
            <Controller
              name="description"
              control={control}
              defaultValue={isNew ? '' : selectedCatalogCode?.description}
              render={({ field }) => (
                <Textbox
                  data-testid="catalog_description_code"
                  {...field}
                  value={field.value ?? ''}
                  aria-label="Description"
                  type="textarea"
                  error={errors?.description}
                />
              )}
            />
            {errors?.description?.message && (
              <ErrorMessage>{errors.description.message}</ErrorMessage>
            )}
          </div>
        </div>

        {hasTagOptions ? (
          <div className="grid-row padding-bottom-2">
            <div className="grid-col-4">
              <h2 className="font-sans-xs">Tags</h2>
              <Controller
                id="catalog_tags"
                name="tags"
                defaultValue={catalogTagDefaultValue}
                control={control}
                render={({ field }) => {
                  return (
                    <MultiSelect
                      data-testid="catalog_tags_sel"
                      key="catalog_tags_sel"
                      name="tags"
                      defaultValue={catalogTagDefaultValue}
                      options={tagOptions}
                      value={field.value || []}
                      onChange={(val, checked) => {
                        let changedValue;
                        if (checked) {
                          changedValue = [...(field.value || []), val];
                        } else {
                          changedValue = field.value.filter((el) => el !== val);
                        }
                        field.onChange(changedValue);
                      }}
                      selectedValues={field.value || []}
                    />
                  );
                }}
              />
              {errors?.tags?.message && (
                <ErrorMessage>{errors.tags.message}</ErrorMessage>
              )}
            </div>
          </div>
        ) : null}

        {metadataOfSelectedCategory?.category === 'Vehicle Group' && (
          <>
            {ability?.can(OPERATIONS.Manage, SUBJECTS.VehicleGroupEngPOCs) && (
              <>
                <ContactForm
                  contactType="PocSeniorEngineer"
                  title="Senior Engineer"
                  required
                />
                <ContactForm
                  contactType="PocPrimaryEngineer"
                  title="Primary Engineer"
                  required
                />
                <ContactForm
                  contactType="PocSecondaryEngineer"
                  title="Secondary Engineer"
                  required={false}
                />
                <ContactForm
                  contactType="PocSeniorCO"
                  title="Senior Contracting Officer"
                  required
                />
                <ContactForm
                  contactType="PocPrimaryCO"
                  title="Primary Contracting Officer"
                  required
                />
                <ContactForm
                  contactType="PocSecondaryCO"
                  title="Secondary Contracting Officer"
                  required={false}
                />
              </>
            )}
          </>
        )}
      </form>
    </FormProvider>
  );
};

CatalogCodeForm.defaultProps = {
  selectedCatalogCode: {},
  metadataOfSelectedCategory: {},
  equipmentCategorySchema: {},
  fuelTypeCategorySchema: {},
  catalogOptionsForTagsAndParentCodes: {},
};

CatalogCodeForm.propTypes = {
  formId: PropTypes.string.isRequired,
  selectedCatalogCode: PropTypes.shape(Object),
  metadataOfSelectedCategory: PropTypes.shape(Object),
  catalogOptionsForTagsAndParentCodes: PropTypes.shape(Object),
  isNew: PropTypes.bool.isRequired,
  equipmentCategorySchema: PropTypes.shape(Object),
  fuelTypeCategorySchema: PropTypes.shape(Object),
  onSubmit: PropTypes.func.isRequired,
  ability: PropTypes.shape(Object).isRequired,
};

export default CatalogCodeForm;
